/**
 * Specialist self-reported availability portal
 *
 * Three states:
 *  1. No claim submitted → ClaimForm (find facility + AHPRA verification)
 *  2. Claim pending moderator review → PendingState
 *  3. Claim approved (profile.claimed = true) → AvailabilityControls
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Search, ChevronDown } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCity } from '@/contexts/CityContext';
import { SEO } from '@/lib/seo';
import { api } from '@/lib/api';
import '@/styles/brand-a.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SpecialistMe {
  profile: {
    id: string;
    accepting_referrals: 'yes' | 'urgent_only' | 'no';
    wait_time_days: number | null;
    specialties: string[];
    special_interests: string[] | null;
    telehealth: boolean;
    accepts_bulk_bill: boolean;
    verified: boolean;
    claimed: boolean;
    onboarding_status: string;
    facility_name: string | null;
    facility_slug: string | null;
  } | null;
  claim: {
    id: string;
    status: string;
    created_at: string;
    facility_name: string | null;
    facility_id: string | null;
  } | null;
}

interface FacilityResult {
  id: string;
  name: string;
  address_json: { suburb?: string; specialty?: string };
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

function useSpecialistMe() {
  return useQuery<SpecialistMe>({
    queryKey: ['specialist-me'],
    queryFn: () => api.get<SpecialistMe>('/v1/specialist/me'),
  });
}

function useDirectorySearch(q: string) {
  return useQuery<{ items: FacilityResult[] }>({
    queryKey: ['directory-search-specialist', q],
    queryFn: () =>
      api
        .get<{ items: FacilityResult[] }>(`/v1/public/canberra/directory?type=specialist&limit=8${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    enabled: q.length >= 2,
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const WAIT_OPTIONS = [
  { label: 'Less than 1 week', value: 5 },
  { label: '1–2 weeks', value: 10 },
  { label: '2–4 weeks', value: 21 },
  { label: '4–8 weeks', value: 42 },
  { label: '8–12 weeks', value: 70 },
  { label: 'More than 3 months', value: 120 },
];

const COMMON_SPECIALTIES = [
  'Cardiology', 'Dermatology', 'Endocrinology', 'Gastroenterology',
  'General Surgery', 'Gynaecology', 'Neurology', 'Oncology',
  'Ophthalmology', 'Orthopaedics', 'Paediatrics', 'Psychiatry',
  'Respiratory', 'Rheumatology', 'Urology', 'Vascular Surgery',
];

// ── AvailabilityControls ──────────────────────────────────────────────────────

function AvailabilityControls({
  profile,
}: {
  profile: NonNullable<SpecialistMe['profile']>;
}) {
  const qc = useQueryClient();
  const [accepting, setAccepting] = useState<'yes' | 'urgent_only' | 'no'>(
    profile.accepting_referrals,
  );
  const [waitDays, setWaitDays] = useState<number | null>(profile.wait_time_days);
  const [specialties, setSpecialties] = useState<string[]>(profile.specialties ?? []);
  const [telehealth, setTelehealth] = useState(profile.telehealth);
  const [bulkBill, setBulkBill] = useState(profile.accepts_bulk_bill);
  const [saved, setSaved] = useState(false);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      api.patch('/v1/specialist/availability', {
        acceptingReferrals: accepting,
        waitTimeDays: waitDays,
        specialties,
        telehealth,
        acceptsBulkBill: bulkBill,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['specialist-me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const toggleSpecialty = (s: string) =>
    setSpecialties((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Verified banner */}
      <div
        style={{
          background: '#E6F5F5', borderRadius: 12, padding: '14px 20px',
          marginBottom: 36, display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <CheckCircle2 size={18} color="#1F8A8A" />
        <div>
          <p style={{ fontFamily: 'Geist', fontSize: 14, fontWeight: 500, color: '#1B2F3C', margin: 0 }}>
            Profile verified — {profile.facility_name}
          </p>
          <p style={{ fontSize: 12, color: '#5A8A8A', margin: 0 }}>
            Your availability is visible to Canberra GPs in the directory.
          </p>
        </div>
      </div>

      {/* Accepting referrals */}
      <div style={{ marginBottom: 32 }}>
        <label
          style={{
            display: 'block', fontFamily: 'Geist', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A7060', marginBottom: 14,
          }}
        >
          Currently accepting referrals
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {(
            [
              { v: 'yes', label: '✓ Yes — taking referrals' },
              { v: 'urgent_only', label: '⚡ Urgent only' },
              { v: 'no', label: '✗ Not currently' },
            ] as const
          ).map(({ v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => setAccepting(v)}
              style={{
                padding: '10px 18px', borderRadius: 10, cursor: 'pointer',
                fontFamily: 'Geist', fontSize: 13, fontWeight: 500,
                background: accepting === v ? '#1B2F3C' : '#FAF8F5',
                color: accepting === v ? '#fff' : '#4A4438',
                border: accepting === v ? '1.5px solid #1B2F3C' : '1.5px solid #DDD8D0',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Wait time */}
      <div style={{ marginBottom: 32 }}>
        <label
          style={{
            display: 'block', fontFamily: 'Geist', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A7060', marginBottom: 14,
          }}
        >
          Typical wait time for new patients
        </label>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <select
            value={waitDays ?? ''}
            onChange={(e) => setWaitDays(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              padding: '11px 36px 11px 14px', borderRadius: 10,
              border: '1.5px solid #DDD8D0', background: '#FAF8F5',
              fontFamily: 'Geist', fontSize: 14, color: '#1B2F3C',
              appearance: 'none', cursor: 'pointer', minWidth: 240,
            }}
          >
            <option value="">Select wait time…</option>
            {WAIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            style={{
              position: 'absolute', right: 12, top: '50%',
              transform: 'translateY(-50%)', pointerEvents: 'none', color: '#7A7060',
            }}
          />
        </div>
      </div>

      {/* Specialties chips */}
      <div style={{ marginBottom: 32 }}>
        <label
          style={{
            display: 'block', fontFamily: 'Geist', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A7060', marginBottom: 14,
          }}
        >
          Specialties I'm currently seeing
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {COMMON_SPECIALTIES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleSpecialty(s)}
              style={{
                padding: '7px 14px', borderRadius: 20, cursor: 'pointer',
                fontFamily: 'Geist', fontSize: 12, fontWeight: 500,
                background: specialties.includes(s) ? '#1F8A8A' : '#FAF8F5',
                color: specialties.includes(s) ? '#fff' : '#4A4438',
                border: specialties.includes(s) ? '1.5px solid #1F8A8A' : '1.5px solid #DDD8D0',
                transition: 'all 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Boolean flags */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 36 }}>
        {[
          { label: 'Telehealth available', val: telehealth, set: setTelehealth },
          { label: 'Bulk billing available', val: bulkBill, set: setBulkBill },
        ].map(({ label, val, set }) => (
          <label
            key={label}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                     fontFamily: 'Geist', fontSize: 14, color: '#4A4438' }}
          >
            <input
              type="checkbox"
              checked={val}
              onChange={(e) => set(e.target.checked)}
              style={{ accentColor: '#1F8A8A', width: 16, height: 16 }}
            />
            {label}
          </label>
        ))}
      </div>

      <button
        type="button"
        onClick={() => save()}
        disabled={isPending}
        className="ba-btn"
        style={{ opacity: isPending ? 0.6 : 1 }}
      >
        {isPending ? 'Saving…' : saved ? '✓ Saved' : 'Save availability'}
      </button>
      {saved && (
        <p style={{ fontSize: 13, color: '#1F8A8A', marginTop: 10, fontFamily: 'Geist' }}>
          Updated — GPs will see your new status within a few minutes.
        </p>
      )}
    </div>
  );
}

// ── ClaimForm ─────────────────────────────────────────────────────────────────

function ClaimForm() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<FacilityResult | null>(null);
  const [ahpra, setAhpra] = useState('');
  const [declaration, setDeclaration] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: searchResults } = useDirectorySearch(searchQuery);

  const { mutate: submitClaim, isPending } = useMutation({
    mutationFn: () =>
      api.post('/v1/specialist/claim', {
        facilityId: selected?.id,
        ahpraNumber: ahpra.toUpperCase().replace(/\s/g, ''),
        declaration: true,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['specialist-me'] }),
    onError: (err: any) =>
      setError(err.response?.data?.message ?? 'Something went wrong. Please try again.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!selected) return setError('Please select your practice from the directory.');
    if (!/^[A-Za-z]{3}\d{10}$/.test(ahpra.replace(/\s/g, '')))
      return setError('AHPRA number must be 3 letters + 10 digits (e.g. MED0001234567).');
    if (!declaration) return setError('Please confirm the declaration to continue.');
    submitClaim();
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 560 }}>
      {/* Facility search */}
      <div style={{ marginBottom: 28 }}>
        <label
          style={{
            display: 'block', fontFamily: 'Geist', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A7060', marginBottom: 10,
          }}
        >
          Find your practice in the directory
        </label>
        <div style={{ position: 'relative' }}>
          <Search
            size={15}
            style={{ position: 'absolute', left: 13, top: '50%',
                     transform: 'translateY(-50%)', color: '#A09880', pointerEvents: 'none' }}
          />
          <input
            type="text"
            placeholder="Search by name or suburb…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelected(null); }}
            style={{
              width: '100%', padding: '12px 14px 12px 38px', borderRadius: 10,
              border: '1.5px solid #DDD8D0', background: '#FAF8F5',
              fontFamily: 'Geist', fontSize: 14, color: '#1B2F3C', outline: 'none',
            }}
          />
        </div>

        {!selected && searchResults && searchResults.items.length > 0 && (
          <div
            style={{
              marginTop: 4, background: '#fff', border: '1.5px solid #DDD8D0',
              borderRadius: 10, overflow: 'hidden',
            }}
          >
            {searchResults.items.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { setSelected(f); setSearchQuery(f.name); }}
                style={{
                  display: 'block', width: '100%', padding: '12px 16px', textAlign: 'left',
                  background: 'none', border: 'none', borderBottom: '1px solid #F5F2EE',
                  cursor: 'pointer',
                }}
              >
                <p style={{ fontFamily: 'Geist', fontSize: 14, fontWeight: 500,
                            color: '#1B2F3C', margin: 0 }}>
                  {f.name}
                </p>
                <p style={{ fontSize: 12, color: '#A09880', margin: 0 }}>
                  {f.address_json?.specialty && `${f.address_json.specialty} · `}
                  {f.address_json?.suburb}
                </p>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div
            style={{
              marginTop: 8, padding: '10px 14px', background: '#E6F5F5',
              borderRadius: 8, display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontFamily: 'Geist', fontSize: 13, color: '#1F8A8A' }}>
              ✓ {selected.name}
            </span>
            <button
              type="button"
              onClick={() => { setSelected(null); setSearchQuery(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                       fontSize: 12, color: '#7A7060' }}
            >
              Change
            </button>
          </div>
        )}
      </div>

      {/* AHPRA number */}
      <div style={{ marginBottom: 28 }}>
        <label
          style={{
            display: 'block', fontFamily: 'Geist', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A7060', marginBottom: 10,
          }}
        >
          AHPRA registration number
        </label>
        <input
          type="text"
          placeholder="e.g. MED0001234567"
          value={ahpra}
          onChange={(e) => setAhpra(e.target.value)}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 10,
            border: '1.5px solid #DDD8D0', background: '#FAF8F5',
            fontFamily: '"Geist Mono", monospace', fontSize: 14, color: '#1B2F3C',
            letterSpacing: '0.05em', outline: 'none',
          }}
        />
        <p style={{ fontSize: 12, color: '#A09880', marginTop: 6, fontFamily: 'Geist' }}>
          Never displayed publicly — used for identity verification only.
        </p>
      </div>

      {/* Declaration */}
      <label
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 24,
        }}
      >
        <input
          type="checkbox"
          checked={declaration}
          onChange={(e) => setDeclaration(e.target.checked)}
          style={{ accentColor: '#1F8A8A', width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ fontFamily: 'Geist', fontSize: 13, color: '#4A4438', lineHeight: 1.5 }}>
          I declare that my AHPRA registration is current and unrestricted, and that I am
          the practitioner listed at this facility.
        </span>
      </label>

      {error && (
        <div
          style={{
            marginBottom: 16, padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 10,
            fontSize: 13, color: '#991b1b', fontFamily: 'Geist',
          }}
        >
          {error}
        </div>
      )}

      <button type="submit" disabled={isPending} className="ba-btn">
        {isPending ? 'Submitting…' : 'Submit claim for review'}
      </button>
      <p style={{ fontSize: 12, color: '#A09880', marginTop: 10, fontFamily: 'Geist' }}>
        A PulseBoard moderator will verify your AHPRA number within 3 business days.
        You'll receive an email when approved.
      </p>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SpecialistAvailability() {
  const { city, link } = useCity();
  const { data, isLoading } = useSpecialistMe();

  const state = !data
    ? 'loading'
    : data.profile?.claimed
      ? 'approved'
      : data.claim
        ? 'pending'
        : 'no_claim';

  return (
    <div className="brand-a" style={{ minHeight: '100dvh' }}>
      <SEO
        title={`Specialist portal · ${city.name} · PulseBoard`}
        description="Update your referral availability for Canberra GPs"
        path="/specialist/availability"
      />

      <nav className="ba-nav" style={{ top: 0 }}>
        <Link to={link('/')} className="ba-wordmark">
          <svg viewBox="0 0 28 28" width="22" height="22" aria-hidden="true">
            <rect x="2" y="2" width="24" height="24" rx="6" fill="#1F8A8A" />
            <path
              d="M7 15 L11 15 L12.5 11 L14.5 19 L16 15 L21 15"
              stroke="#F5F1EB" strokeWidth="1.7" fill="none"
              strokeLinecap="round" strokeLinejoin="round"
            />
            <circle cx="21" cy="15" r="1.3" fill="#F27E62" />
          </svg>
          Pulseboard
          <span className="ba-wordmark__city">{city.name}</span>
        </Link>
        <ul className="ba-nav__links">
          <li><Link to={link('/directory')}>Directory</Link></li>
          <li><Link to={link('/resources')}>Resources</Link></li>
        </ul>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link to={link('/auth')} className="ba-btn ba-btn--ghost ba-btn--sm">Sign out</Link>
        </div>
      </nav>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 32px 80px' }}>
        <p className="ba-eyebrow" style={{ marginBottom: 12 }}>Specialist portal</p>
        <h1
          style={{
            fontFamily: 'var(--ds-font-display)', fontSize: 'clamp(32px, 4vw, 48px)',
            color: '#1B2F3C', marginBottom: 12,
          }}
        >
          {state === 'approved' ? 'Your availability' : 'Claim your profile'}
        </h1>

        {state !== 'approved' && (
          <p
            style={{
              fontFamily: 'Geist', fontSize: 15, color: '#4A4438', lineHeight: 1.6,
              maxWidth: 520, marginBottom: 8,
            }}
          >
            Canberra GPs use PulseBoard to find specialists before referring.
            Claim your profile to control your availability, sub-specialties,
            and referral preferences.
          </p>
        )}
        <div className="ba-underline" style={{ marginBottom: 40 }} />

        {isLoading && (
          <div
            style={{
              height: 200, background: '#EDE9E2', borderRadius: 12,
              animation: 'shimmer 1.5s infinite',
            }}
          />
        )}

        {state === 'no_claim' && <ClaimForm />}

        {state === 'pending' && data?.claim && (
          <div
            style={{
              maxWidth: 480, padding: '28px 32px', background: '#FAF8F5',
              borderRadius: 16, border: '1px solid #DDD8D0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <Clock size={20} color="#F27E62" />
              <h2 style={{ fontFamily: 'Geist', fontSize: 18, fontWeight: 600,
                           color: '#1B2F3C', margin: 0 }}>
                Claim under review
              </h2>
            </div>
            <p style={{ fontFamily: 'Geist', fontSize: 14, color: '#4A4438',
                        lineHeight: 1.6, marginBottom: 10 }}>
              Your claim for{' '}
              <strong>{data.claim.facility_name ?? 'your practice'}</strong> was submitted on{' '}
              {new Date(data.claim.created_at).toLocaleDateString('en-AU', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
              .
            </p>
            <p style={{ fontSize: 13, color: '#7A7060', fontFamily: 'Geist' }}>
              A moderator will verify your AHPRA number within 3 business days.
              You'll receive an email at the address on your account when approved.
            </p>
          </div>
        )}

        {state === 'approved' && data?.profile && (
          <AvailabilityControls profile={data.profile} />
        )}
      </div>
    </div>
  );
}
